/**
 * ZenMarkup Privacy Analytics — Map Controls (pan & zoom for SVG world map).
 */

import { bind as bindTouchControls } from './map-touch.js';

const ZOOM_STEP = 0.3;
const ZOOM_MIN = 1;
const ZOOM_MAX = 6;

let container = null;
let svg = null;

let scale = 1;
let translateX = 0;
let translateY = 0;
let isDragging = false;
let dragStartX = 0;
let dragStartY = 0;
let dragStartTX = 0;
let dragStartTY = 0;

/** Apply transform to SVG. */
function applyTransform() {
	if (!svg) return;
	svg.style.transform = 'translate(' + translateX + 'px, ' + translateY + 'px) scale(' + scale + ')';
}

/** Clamp translation so map stays within bounds. */
function clampTranslation() {
	if (!container || !svg) return;
	const containerW = container.clientWidth;
	const containerH = container.clientHeight;
	const svgRect = svg.getBoundingClientRect();
	const scaledW = (svgRect.width / scale) * scale;
	const scaledH = (svgRect.height / scale) * scale;
	const minX = Math.min(0, containerW - scaledW);
	const minY = Math.min(0, containerH - scaledH);
	translateX = Math.max(minX, Math.min(0, translateX));
	translateY = Math.max(minY, Math.min(0, translateY));
}

/** Zoom around a given point (cx, cy in container coords). */
function zoomAround(prevScale, cx, cy) {
	translateX = cx - (cx - translateX) * (scale / prevScale);
	translateY = cy - (cy - translateY) * (scale / prevScale);
	clampTranslation();
	applyTransform();
}

/** Zoom toward the container center. */
function zoomCenter(prevScale) {
	const cx = container.clientWidth / 2;
	const cy = container.clientHeight / 2;
	zoomAround(prevScale, cx, cy);
}

/** Reset zoom and pan. */
function resetView() {
	scale = 1;
	translateX = 0;
	translateY = 0;
	applyTransform();
}

/** Bind zoom buttons. */
function bindButtons() {
	const btnIn = document.querySelector('[data-el="map-zoom-in"]');
	const btnOut = document.querySelector('[data-el="map-zoom-out"]');
	const btnReset = document.querySelector('[data-el="map-reset"]');

	if (btnIn) {
		btnIn.addEventListener('click', function () {
			const prev = scale;
			scale = Math.min(ZOOM_MAX, scale + ZOOM_STEP);
			zoomCenter(prev);
		});
	}

	if (btnOut) {
		btnOut.addEventListener('click', function () {
			const prev = scale;
			scale = Math.max(ZOOM_MIN, scale - ZOOM_STEP);
			zoomCenter(prev);
		});
	}

	if (btnReset) {
		btnReset.addEventListener('click', resetView);
	}
}

/** Bind mouse drag for panning (only when zoomed in). */
function bindPan() {
	container.addEventListener('mousedown', function (e) {
		if (scale <= 1) {
			return;
		}
		if (e.target.closest('.zmpa-map-tooltip')) {
			return;
		}

		isDragging = true;
		dragStartX = e.clientX;
		dragStartY = e.clientY;
		dragStartTX = translateX;
		dragStartTY = translateY;
		svg.classList.add('zmpa-map-panning');
		container.classList.add('zmpa-map-dragging');
		e.preventDefault();
	});

	document.addEventListener('mousemove', function (e) {
		if (!isDragging) {
			return;
		}

		translateX = dragStartTX + (e.clientX - dragStartX);
		translateY = dragStartTY + (e.clientY - dragStartY);
		clampTranslation();
		applyTransform();
	});

	document.addEventListener('mouseup', function () {
		if (isDragging) {
			isDragging = false;
			svg.classList.remove('zmpa-map-panning');
			container.classList.remove('zmpa-map-dragging');
		}
	});
}

/** Bind mouse wheel: Ctrl+scroll zooms, plain scroll passes through. */
function bindWheel() {
	container.addEventListener('wheel', function (e) {
		if (!e.ctrlKey) {
			return;
		}

		e.preventDefault();
		const prev = scale;

		if (e.deltaY < 0) {
			scale = Math.min(ZOOM_MAX, scale + ZOOM_STEP);
		} else {
			scale = Math.max(ZOOM_MIN, scale - ZOOM_STEP);
		}

		const rect = container.getBoundingClientRect();
		zoomAround(prev, e.clientX - rect.left, e.clientY - rect.top);
	}, { passive: false });
}

/** Delegate touch pinch-to-zoom and pan to map-touch module. */
function bindTouch() {
	bindTouchControls({
		container: container,
		svg: svg,
		ZOOM_MIN: ZOOM_MIN,
		ZOOM_MAX: ZOOM_MAX,
		getScale: function () { return scale; },
		setScale: function (s) { scale = s; },
		getTranslate: function () { return { x: translateX, y: translateY }; },
		setTranslate: function (x, y) { translateX = x; translateY = y; },
		clampAndApply: function () { clampTranslation(); applyTransform(); },
		zoomAround: zoomAround
	});
}

/** Swap hint text on touch devices. */
function updateHint() {
	const isTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
	if (!isTouch) return;
	const hint = document.querySelector('[data-el="map-hint"]');
	if (hint && hint.dataset.touchLabel) {
		hint.textContent = hint.dataset.touchLabel;
	}
}

export function init(mapContainer, mapSvg) {
	container = mapContainer;
	svg = mapSvg;
	bindButtons();
	bindPan();
	bindWheel();
	bindTouch();
	updateHint();
}
